import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface MoistureCorrectionRecord {
  id?: number;
  recordNumber: string;
  correctionDate: Date;
  performedBy: number | null;
  shift: "day" | "night" | "swing";

  // Material Being Tested
  materialId: number | null;
  materialName: string;
  binLocation: string;
  batchNumber: string;

  // Moisture Testing
  sampleWeight: number; // grams
  dryWeight: number; // grams
  moistureContent: number; // percentage
  targetMoisture: number; // percentage
  moistureTolerance: number; // percentage

  // Correction
  correctionNeeded: boolean;
  waterAdjustment: number; // gallons per cubic yard
  correctionApplied: boolean;
  correctionApprovedBy: string;

  // Verification
  retestRequired: boolean;
  retestMoisture: number | null;
  retestPassed: boolean;

  notes: string;
  status: "draft" | "recorded" | "corrected" | "verified";
}

class MoistureCorrectionWorkflowStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  correctionData: MoistureCorrectionRecord;

  constructor(rootStore: RootStore) {
    this.correctionData = this.getDefaultCorrection();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultCorrection = (): MoistureCorrectionRecord => ({
    recordNumber: this.generateRecordNumber(),
    correctionDate: new Date(),
    performedBy: null,
    shift: "day",

    materialId: null,
    materialName: "",
    binLocation: "",
    batchNumber: "",

    sampleWeight: 0,
    dryWeight: 0,
    moistureContent: 0,
    targetMoisture: 4,
    moistureTolerance: 1,

    correctionNeeded: false,
    waterAdjustment: 0,
    correctionApplied: false,
    correctionApprovedBy: "",

    retestRequired: false,
    retestMoisture: null,
    retestPassed: true,

    notes: "",
    status: "draft",
  });

  generateRecordNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    return `MC${year}${month}${day}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateCorrectionData = <K extends keyof MoistureCorrectionRecord>(
    field: K,
    value: MoistureCorrectionRecord[K]
  ) => {
    this.correctionData[field] = value;

    // Auto-calculate moisture content
    if (field === "sampleWeight" || field === "dryWeight") {
      this.calculateMoistureContent();
    }
  };

  calculateMoistureContent = () => {
    if (this.correctionData.sampleWeight > 0 && this.correctionData.dryWeight > 0) {
      const moisture = ((this.correctionData.sampleWeight - this.correctionData.dryWeight) / this.correctionData.dryWeight) * 100;
      this.correctionData.moistureContent = parseFloat(moisture.toFixed(2));

      // Check if correction is needed
      const diff = Math.abs(this.correctionData.moistureContent - this.correctionData.targetMoisture);
      this.correctionData.correctionNeeded = diff > this.correctionData.moistureTolerance;
    }
  };

  validateCurrentStep = (): boolean => {
    this.setError(null);
    if (this.activeStep === 0 && !this.correctionData.materialId) {
      this.setError("Please select a material");
      return false;
    }
    return true;
  };

  loadInitialData = async () => {
    this.setLoading(true);
    try {
      const materialsStore = this.rootStore.materialsStore as ServiceStore;
      await materialsStore?.find();
    } catch (err: any) {
      runInAction(() => this.setError("Failed to load data"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  submitCorrection = async () => {
    this.setLoading(true);
    try {
      runInAction(() => {
        this.setSuccess("Moisture correction recorded successfully!");
        this.activeStep = 3;
      });
    } catch (err: any) {
      runInAction(() => this.setError(err.message || "Failed to submit"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.correctionData = this.getDefaultCorrection();
  };

  get materials() {
    const materialsStore = this.rootStore.materialsStore as ServiceStore;
    return materialsStore?.entities || [];
  }
}

export default MoistureCorrectionWorkflowStore;
